import { useEffect, useState } from "react";
import { Dialog, Snackbar, Alert, Tooltip, Tabs, Tab, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import styles from "./companyprofile.module.css";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import { CountryList } from "../../components/Constants";
import { ArrowRight, Briefcase, Copy, CreditCard, Edit2, FileText, Globe, Layout, Mail, Phone, Plus, Send, Server, Truck } from "react-feather";
import { StorageSetItem } from "../../components/StorageComp";
import CopyToClipboard from "react-copy-to-clipboard";
import axios from "axios";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import UpdateCompanyForm from "../../components/popups/UpdateCompanyForm";
import SmtpSettings from "./SmtpSettings";
import MailjetSettings from "./MailjetSettings";
import ChargeSettings from "./ChargeSettings";

const TABS = ["profile", "email", "charges"];

const countryLabel = (code) => CountryList?.find((i) => i?.value === code)?.label || code;

const money = (value) => (value !== null && value !== undefined && value !== "" ? `£${Number(value).toFixed(2)}` : null);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

// Fields counted towards the "profile complete" meter in the summary card
const PROFILE_FIELDS = ["company_name", "company_address", "name", "email", "phone", "reg_number", "vat_number", "payment_receiver_name", "payment_receiver_account", "terms_condition"];

const Field = ({ label, value, href, onAdd, full }) => (
  <div className={`${styles.Field} ${full ? styles.FieldFull : ""}`}>
    <dt>{label}</dt>
    <dd>
      {hasValue(value)
        ? (href ? <a href={href} className={`${styles.FieldLink}`}>{value}</a> : value)
        : <button type="button" className={`${styles.AddLink}`} onClick={onAdd}><Plus /> Add</button>}
    </dd>
  </div>
);

const SectionCard = ({ icon, title, onEdit, children, full }) => (
  <section className={`${styles.Card} ${full ? styles.CardFull : ""}`}>
    <div className={`${styles.CardHead} ${styles.CardHeadCenter}`}>
      <h2 className={`${styles.CardTitle}`}><span className={`${styles.IconBadge}`}>{icon}</span>{title}</h2>
      <button type="button" className={`${styles.EditBU}`} onClick={onEdit} aria-label={`Edit ${title.toLowerCase()}`}><Edit2 /> Edit</button>
    </div>
    <div className={`${styles.CardBody}`}>{children}</div>
  </section>
);

const CompanyProfile = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpenCompanyProfile, setFormOpenCompanyProfile] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [settings, setSettings] = useState(null);
  // Provider shown in the Email tab; starts on the active one, saving a provider's form makes it active
  const [emailProvider, setEmailProvider] = useState("smtp");
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [domainCopied, setDomainCopied] = useState(false);

  const tab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "profile";

  const { websiteData } = useWebsite();
  useSiteBranding("Company settings", websiteData);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${API_TOKEN}` };
    const companyId = authData()?.company?.id;

    if (!companyId) {
      setLoading(false);
      return;
    }

    Promise.allSettled([
      axios.get(process.env.REACT_APP_API_URL + "v1/companies/" + companyId, { headers }),
      axios.get(process.env.REACT_APP_API_URL + "v1/company-settings", { headers }),
    ]).then(([companyRes, settingsRes]) => {
      if (companyRes.status === "fulfilled") {
        setCompanyData(companyRes.value.data.data);
      }
      if (settingsRes.status === "fulfilled" && settingsRes.value.data.status === "SUCCESS") {
        setSettings(settingsRes.value.data.data);
        setEmailProvider(settingsRes.value.data.data?.mail_provider || "smtp");
      }
      setLoading(false);
    });
  }, [API_TOKEN]);

  const showMessage = (message, type = "success") => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  const onCompanyUpdate = (data) => {
    setCompanyData(data);

    let authTemp = auth;
    let companyData = { id: data?.id, abb: data?.abb, company_name: data?.company_name, domain: data?.domain, pickup_country: data?.pickup_country, delivery_country: data?.delivery_country, urltoken: data?.urltoken }
    authTemp = { ...authTemp, company: companyData };

    StorageSetItem("bookAuthData", authTemp, 24 * 60 * 60 * 1000);
  }

  const onTabChange = (e, value) => {
    setSearchParams(value === "profile" ? {} : { tab: value }, { replace: true });
  };

  const onDomainCopy = () => {
    setDomainCopied(true);
    setTimeout(() => setDomainCopied(false), 1500);
  };

  const openEdit = () => companyData && setFormOpenCompanyProfile(true);

  const initial = (companyData?.company_name || "?").trim().charAt(0).toUpperCase();
  const terms = companyData?.terms_condition;
  const filled = PROFILE_FIELDS.filter((k) => hasValue(companyData?.[k])).length;
  const completion = Math.round((filled / PROFILE_FIELDS.length) * 100);

  return (<div>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <PageHeader title="Company Settings" />

    <div className={`${styles.MainDiv}`}>
      <div className={`${styles.Container}`}>
        <div className={`${styles.SummaryCard}`}>
          <div className={`${styles.Avatar}`}>
            {companyData?.logo_url ? <img src={companyData.logo_url} alt="" /> : initial}
          </div>
          <div className={`${styles.SummaryText}`}>
            <p className={`${styles.CompanyName}`}>{companyData?.company_name || "Company"}</p>
            {companyData?.domain && (
              <CopyToClipboard text={companyData.domain} onCopy={onDomainCopy}>
                <Tooltip title={domainCopied ? "Copied!" : "Click to copy"}>
                  <button type="button" className={`${styles.DomainChip}`} aria-label={`Copy domain ${companyData.domain}`}>
                    <Globe />
                    <span>{companyData.domain}</span>
                    <Copy />
                  </button>
                </Tooltip>
              </CopyToClipboard>
            )}
          </div>
          {companyData && (
            <div className={`${styles.Completion}`}>
              <div className={`${styles.CompletionText}`}>
                <span>Profile completeness</span>
                <strong>{completion}%</strong>
              </div>
              <div className={`${styles.CompletionTrack}`} role="progressbar" aria-valuenow={completion} aria-valuemin={0} aria-valuemax={100} aria-label="Profile completeness">
                <div className={`${styles.CompletionBar}`} style={{ width: `${completion}%` }} />
              </div>
            </div>
          )}
          <div className={`${styles.SummaryActions}`}>
            <RouterLink className={`${styles.ActionBU} ${styles.ActionGhost}`} to={"/company-template"}><Layout /> Website Template</RouterLink>
          </div>
        </div>

        <div className={`${styles.TabsBar}`}>
          <Tabs value={tab} onChange={onTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Company settings sections">
            <Tab value="profile" icon={<Briefcase />} iconPosition="start" label="Profile" />
            <Tab value="email" icon={<Mail />} iconPosition="start" label="Email" />
            <Tab value="charges" icon={<Truck />} iconPosition="start" label="Collection & Delivery" />
          </Tabs>
        </div>

        {tab === "profile" && (
          <div className={`${styles.Grid}`}>
            <SectionCard icon={<Briefcase />} title="Business details" onEdit={openEdit}>
              <dl className={`${styles.FieldGrid}`}>
                <Field label="Company name" value={companyData?.company_name} onAdd={openEdit} full />
                <Field label="Registration no." value={companyData?.reg_number} onAdd={openEdit} />
                <Field label="VAT number" value={companyData?.vat_number} onAdd={openEdit} />
                <Field label="Address" value={companyData?.company_address} onAdd={openEdit} full />
              </dl>
            </SectionCard>

            <SectionCard icon={<Phone />} title="Contact" onEdit={openEdit}>
              <dl className={`${styles.FieldGrid}`}>
                <Field label="Owner" value={companyData?.name} onAdd={openEdit} full />
                <Field label="Email" value={companyData?.email} href={`mailto:${companyData?.email}`} onAdd={openEdit} full />
                <Field label="Phone" value={companyData?.phone} href={`tel:${companyData?.phone}`} onAdd={openEdit} full />
              </dl>
            </SectionCard>

            <SectionCard icon={<CreditCard />} title="Tax & payments" onEdit={openEdit}>
              <div className={`${styles.StatRow}`}>
                <div className={`${styles.Stat}`}>
                  <span>{companyData?.tax_title || "Tax"}</span>
                  <strong>{companyData?.tax_charges > 0 ? `${Number(companyData.tax_charges).toFixed(2)}%` : "—"}</strong>
                </div>
                <div className={`${styles.Stat}`}>
                  <span>Insurance</span>
                  <strong>{companyData?.insurance_charges > 0 ? money(companyData.insurance_charges) : "—"}</strong>
                </div>
                <div className={`${styles.Stat}`}>
                  <span>Reference</span>
                  <strong>{money(companyData?.reference_amount) || "—"}</strong>
                </div>
              </div>
              <dl className={`${styles.FieldGrid}`}>
                <Field label="Receiver name" value={companyData?.payment_receiver_name} onAdd={openEdit} full />
                <Field label="Receiver account" value={companyData?.payment_receiver_account} onAdd={openEdit} />
                <Field label="Receiver code" value={companyData?.payment_receiver_code} onAdd={openEdit} />
              </dl>
            </SectionCard>

            <SectionCard icon={<Truck />} title="Shipping route" onEdit={openEdit}>
              <div className={`${styles.Route}`}>
                <div className={`${styles.RouteStop}`}>
                  <span>Pickup from</span>
                  <strong>{companyData?.pickup_country ? countryLabel(companyData.pickup_country) : "Not set"}</strong>
                </div>
                <span className={`${styles.RouteArrow}`}><ArrowRight /></span>
                <div className={`${styles.RouteStop}`}>
                  <span>Deliver to</span>
                  <strong>{companyData?.delivery_country ? countryLabel(companyData.delivery_country) : "Not set"}</strong>
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={<FileText />} title="Terms & conditions" onEdit={openEdit} full>
              {terms ? (<>
                <p className={`${styles.Terms} ${termsExpanded ? "" : styles.TermsClamp}`}>{terms}</p>
                {terms.length > 300 && (
                  <button type="button" className={`${styles.LinkBU}`} onClick={() => setTermsExpanded((v) => !v)}>{termsExpanded ? "Show less" : "Show more"}</button>
                )}
              </>) : (
                <div className={`${styles.EmptyState}`}>
                  <p>No terms & conditions added yet.</p>
                  <button type="button" className={`${styles.AddLink}`} onClick={openEdit}><Plus /> Add terms</button>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {tab === "email" && (
          <>
            <ToggleButtonGroup exclusive value={emailProvider} onChange={(e, v) => v && setEmailProvider(v)} className={`${styles.TypeToggle}`} aria-label="Email provider">
              <ToggleButton value="smtp"><Server /> SMTP{settings?.mail_provider === "smtp" ? " (active)" : ""}</ToggleButton>
              <ToggleButton value="mailjet"><Send /> Mailjet{settings?.mail_provider === "mailjet" ? " (active)" : ""}</ToggleButton>
            </ToggleButtonGroup>
            {emailProvider === "smtp"
              ? <SmtpSettings settings={settings?.smtp} active={settings?.mail_provider === "smtp"} defaultTestEmail={companyData?.email} onSaved={(data) => setSettings(data)} showMessage={showMessage} />
              : <MailjetSettings settings={settings?.mailjet} active={settings?.mail_provider === "mailjet"} defaultTestEmail={companyData?.email} onSaved={(data) => setSettings(data)} showMessage={showMessage} />}
          </>
        )}

        {tab === "charges" && (
          <ChargeSettings charges={settings?.charges} onSaved={(data) => setSettings(data)} showMessage={showMessage} />
        )}
      </div>
    </div>

    <Dialog open={formOpenCompanyProfile} onClose={(e) => setFormOpenCompanyProfile(false)} fullWidth maxWidth="sm">
      <UpdateCompanyForm setFormOpen={setFormOpenCompanyProfile.bind(this)} onLocal={onCompanyUpdate.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={companyData} />
    </Dialog>

    <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={(e) => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
      <Alert onClose={(e) => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>{snackbarMessage}</Alert>
    </Snackbar>
  </div>);
};

export default withAdminAuth(CompanyProfile);
